import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    Search,
    Users,
    Shield,
    UserCheck,
    User,
    Edit,
    MoreVertical,
    UserPlus,
    KeyRound,
    Copy,
    RefreshCcw,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

// Generate a memorable-ish random password (8 lowercase letters + 2 digits).
const generatePassword = () => {
    const chars = "abcdefghjkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 10; i++) {
        out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
};

const UserManagement = () => {
    const { token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [editingUser, setEditingUser] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isResetOpen, setIsResetOpen] = useState(false);
    const [resetPwd, setResetPwd] = useState("");
    const [resetSaving, setResetSaving] = useState(false);
    const [newUser, setNewUser] = useState({
        email: "",
        full_name: "",
        role: "content_manager",
        password: generatePassword(),
    });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, [roleFilter]);

    const fetchUsers = async () => {
        try {
            let url = `${API}/users?limit=100`;
            if (roleFilter && roleFilter !== "all") url += `&role=${roleFilter}`;
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data);
        } catch (error) {
            toast.error("Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async (userId, updates) => {
        try {
            await axios.put(`${API}/users/${userId}`, updates, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("User updated");
            fetchUsers();
            setIsDialogOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to update user");
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.full_name || !newUser.password) {
            toast.error("Email, full name and password are required");
            return;
        }
        setCreating(true);
        try {
            await axios.post(`${API}/users`, newUser, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`System user created — password: ${newUser.password}`);
            setIsCreateOpen(false);
            setNewUser({ email: "", full_name: "", role: "content_manager", password: generatePassword() });
            fetchUsers();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to create user");
        } finally {
            setCreating(false);
        }
    };

    const handleResetPassword = async () => {
        if (!editingUser || !resetPwd || resetPwd.length < 4) {
            toast.error("Password must be at least 4 characters");
            return;
        }
        setResetSaving(true);
        try {
            await axios.post(`${API}/users/${editingUser.id}/reset-password`, { new_password: resetPwd }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Password reset. New password: ${resetPwd} (copy now, this is the last time it's shown)`);
            setIsResetOpen(false);
            setResetPwd("");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to reset password");
        } finally {
            setResetSaving(false);
        }
    };

    const copyPassword = (pwd) => {
        try { navigator.clipboard.writeText(pwd); toast.success("Password copied to clipboard"); }
        catch (_) { toast.error("Copy failed — select the text manually"); }
    };

    const handleToggleStatus = async (user) => {
        await handleUpdateUser(user.id, { is_active: !user.is_active });
    };

    const handleChangeRole = async (user, newRole) => {
        await handleUpdateUser(user.id, { role: newRole });
    };

    const filteredUsers = users.filter(user => 
        user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getRoleIcon = (role) => {
        switch (role) {
            case "admin": return <Shield className="h-4 w-4 text-electric" />;
            case "content_manager": return <UserCheck className="h-4 w-4 text-solar" />;
            default: return <User className="h-4 w-4 text-white/50" />;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
    };

    return (
        <AdminLayout title="System Users">
            <div className="space-y-6" data-testid="user-management">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-electric/10 flex items-center justify-center">
                                <Users className="h-5 w-5 text-electric" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Total Users</p>
                                <p className="text-2xl font-heading font-bold text-white">{users.length}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-electric/10 flex items-center justify-center">
                                <Shield className="h-5 w-5 text-electric" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Admins</p>
                                <p className="text-2xl font-heading font-bold text-electric">
                                    {users.filter(u => u.role === "admin").length}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-solar/10 flex items-center justify-center">
                                <UserCheck className="h-5 w-5 text-solar" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Content Managers</p>
                                <p className="text-2xl font-heading font-bold text-solar">
                                    {users.filter(u => u.role === "content_manager").length}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                                <User className="h-5 w-5 text-white/50" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Regular Users</p>
                                <p className="text-2xl font-heading font-bold text-white">
                                    {users.filter(u => u.role === "user").length}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="flex gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <Input
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 bg-paper border-border text-white w-64"
                                data-testid="user-search"
                            />
                        </div>
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="w-40 bg-paper border-border text-white" data-testid="role-filter">
                                <SelectValue placeholder="All Roles" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Roles</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="subadmin">Sub-admin</SelectItem>
                                <SelectItem value="supervisor">Supervisor</SelectItem>
                                <SelectItem value="content_manager">Content Manager</SelectItem>
                                <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        onClick={() => {
                            setNewUser({ email: "", full_name: "", role: "content_manager", password: generatePassword() });
                            setIsCreateOpen(true);
                        }}
                        className="bg-electric hover:bg-electric/80 text-white font-bold"
                        data-testid="new-user-btn"
                    >
                        <UserPlus className="h-4 w-4 mr-2" /> New System User
                    </Button>
                </div>

                {/* Users List */}
                {loading ? (
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <Card key={i} className="bg-paper border-border animate-pulse">
                                <CardContent className="p-4 flex gap-4">
                                    <div className="w-12 h-12 bg-muted rounded-full" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-5 bg-muted rounded w-1/3" />
                                        <div className="h-4 bg-muted rounded w-1/4" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredUsers.length > 0 ? (
                    <div className="space-y-3">
                        {filteredUsers.map((user, idx) => (
                            <Card 
                                key={user.id} 
                                className="bg-paper border-border hover:border-border/80 transition-colors"
                                data-testid={`user-item-${idx}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-electric flex items-center justify-center shrink-0">
                                            <span className="text-white font-bold text-lg">
                                                {user.full_name?.charAt(0) || "U"}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-white font-medium">
                                                    {user.full_name}
                                                </h3>
                                                {getRoleIcon(user.role)}
                                            </div>
                                            <p className="text-white/50 text-sm truncate">{user.email}</p>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <Badge 
                                                variant="outline"
                                                className={
                                                    user.role === "admin" 
                                                        ? "border-electric text-electric"
                                                        : user.role === "content_manager"
                                                        ? "border-solar text-solar"
                                                        : "border-white/30 text-white/50"
                                                }
                                            >
                                                {user.role.replace("_", " ")}
                                            </Badge>
                                            <Badge 
                                                variant="outline"
                                                className={
                                                    user.is_active 
                                                        ? "border-green-500 text-green-400"
                                                        : "border-red-500 text-red-400"
                                                }
                                            >
                                                {user.is_active ? "Active" : "Inactive"}
                                            </Badge>
                                            <span className="text-white/30 text-xs">
                                                {formatDate(user.created_at)}
                                            </span>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="sm" className="text-white/50">
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="bg-paper border-border">
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            setEditingUser(user);
                                                            setIsDialogOpen(true);
                                                        }}
                                                        className="text-white hover:bg-subtle"
                                                    >
                                                        <Edit className="h-4 w-4 mr-2" />
                                                        Edit User
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => handleToggleStatus(user)}
                                                        className={user.is_active ? "text-red-400" : "text-green-400"}
                                                    >
                                                        {user.is_active ? "Deactivate" : "Activate"}
                                                    </DropdownMenuItem>
                                                    {user.role !== "admin" && (
                                                        <DropdownMenuItem
                                                            onClick={() => handleChangeRole(user, "admin")}
                                                            className="text-electric"
                                                        >
                                                            <Shield className="h-4 w-4 mr-2" />
                                                            Make Admin
                                                        </DropdownMenuItem>
                                                    )}
                                                    {user.role !== "content_manager" && (
                                                        <DropdownMenuItem
                                                            onClick={() => handleChangeRole(user, "content_manager")}
                                                            className="text-solar"
                                                        >
                                                            <UserCheck className="h-4 w-4 mr-2" />
                                                            Make Content Manager
                                                        </DropdownMenuItem>
                                                    )}
                                                    {user.role !== "user" && (
                                                        <DropdownMenuItem
                                                            onClick={() => handleChangeRole(user, "user")}
                                                            className="text-white/50"
                                                        >
                                                            <User className="h-4 w-4 mr-2" />
                                                            Make Regular User
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-paper border-border">
                        <CardContent className="p-12 text-center">
                            <Users className="h-16 w-16 text-white/20 mx-auto mb-4" />
                            <h3 className="text-white font-heading text-xl uppercase mb-2">No Users Found</h3>
                            <p className="text-white/50">Try adjusting your search or filters</p>
                        </CardContent>
                    </Card>
                )}

                {/* Edit Dialog */}
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className="bg-paper border-border">
                        <DialogHeader>
                            <DialogTitle className="text-white font-heading uppercase">
                                Edit User
                            </DialogTitle>
                        </DialogHeader>
                        {editingUser && (
                            <div className="space-y-4 mt-4">
                                <div>
                                    <Label className="text-white">Email (login username)</Label>
                                    <Input
                                        type="email"
                                        value={editingUser.email || ""}
                                        onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="edit-user-email"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Full Name</Label>
                                    <Input
                                        value={editingUser.full_name}
                                        onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="edit-user-name"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Role</Label>
                                    <Select 
                                        value={editingUser.role} 
                                        onValueChange={(v) => setEditingUser({...editingUser, role: v})}
                                    >
                                        <SelectTrigger className="bg-subtle border-border text-white" data-testid="edit-user-role">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="admin">Admin</SelectItem>
                                            <SelectItem value="subadmin">Sub-admin</SelectItem>
                                            <SelectItem value="supervisor">Supervisor</SelectItem>
                                            <SelectItem value="content_manager">Content Manager</SelectItem>
                                            <SelectItem value="user">User</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="pt-2 border-t border-border">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setResetPwd(generatePassword());
                                            setIsResetOpen(true);
                                        }}
                                        className="w-full border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                                        data-testid="reset-password-btn"
                                    >
                                        <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                                    </Button>
                                    <p className="text-white/40 text-[11px] mt-1.5 text-center">
                                        Generates a new password and shows it once so you can share it with the user.
                                    </p>
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <Button
                                        variant="ghost"
                                        onClick={() => setIsDialogOpen(false)}
                                        className="text-white/60"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => handleUpdateUser(editingUser.id, {
                                            email: editingUser.email,
                                            full_name: editingUser.full_name,
                                            role: editingUser.role
                                        })}
                                        className="btn-primary"
                                        data-testid="save-user-btn"
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Create User Dialog */}
                <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                    <DialogContent className="bg-paper border-border" data-testid="create-user-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-white font-heading uppercase flex items-center gap-2">
                                <UserPlus className="h-5 w-5 text-electric" /> New System User
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="text-white">Email (login username)</Label>
                                <Input
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                    placeholder="user@dimbani.com"
                                    className="bg-subtle border-border text-white"
                                    data-testid="new-user-email"
                                />
                            </div>
                            <div>
                                <Label className="text-white">Full Name</Label>
                                <Input
                                    value={newUser.full_name}
                                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                                    placeholder="Jina kamili"
                                    className="bg-subtle border-border text-white"
                                    data-testid="new-user-name"
                                />
                            </div>
                            <div>
                                <Label className="text-white">Role (access level)</Label>
                                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                                    <SelectTrigger className="bg-subtle border-border text-white" data-testid="new-user-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">Admin — full access</SelectItem>
                                        <SelectItem value="subadmin">Sub-admin — everything except manage users/settings</SelectItem>
                                        <SelectItem value="supervisor">Supervisor — approvals + analytics</SelectItem>
                                        <SelectItem value="content_manager">Content Manager — content + basic analytics</SelectItem>
                                        <SelectItem value="user">User — public site only</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-white/40 text-[11px] mt-1">
                                    Detailed permissions per role can be tuned in Control &amp; Management → Access Levels.
                                </p>
                            </div>
                            <div>
                                <Label className="text-white">Initial Password</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={newUser.password}
                                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                                        className="bg-subtle border-border text-white font-mono"
                                        data-testid="new-user-password"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setNewUser({...newUser, password: generatePassword()})}
                                        title="Regenerate"
                                        data-testid="new-user-regen"
                                    >
                                        <RefreshCcw className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => copyPassword(newUser.password)}
                                        title="Copy"
                                        data-testid="new-user-copy"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                                <p className="text-white/40 text-[11px] mt-1">
                                    Share this with the user — they can change it after logging in.
                                </p>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="ghost" onClick={() => setIsCreateOpen(false)} className="text-white/60">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleCreateUser}
                                    disabled={creating}
                                    className="btn-primary"
                                    data-testid="create-user-btn"
                                >
                                    {creating ? "Creating…" : <><UserPlus className="h-4 w-4 mr-2" /> Create User</>}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Reset Password Dialog */}
                <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
                    <DialogContent className="bg-paper border-border" data-testid="reset-password-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-white font-heading uppercase flex items-center gap-2">
                                <KeyRound className="h-5 w-5 text-yellow-300" /> Reset Password
                            </DialogTitle>
                        </DialogHeader>
                        {editingUser && (
                            <div className="space-y-4 mt-4">
                                <p className="text-white/70 text-sm">
                                    Resetting password for <span className="text-white font-medium">{editingUser.full_name}</span>{" "}
                                    <span className="text-white/40">({editingUser.email})</span>
                                </p>
                                <div>
                                    <Label className="text-white">New Password</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={resetPwd}
                                            onChange={(e) => setResetPwd(e.target.value)}
                                            className="bg-subtle border-border text-white font-mono"
                                            data-testid="reset-pwd-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setResetPwd(generatePassword())}
                                            title="Regenerate"
                                            data-testid="reset-pwd-regen"
                                        >
                                            <RefreshCcw className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => copyPassword(resetPwd)}
                                            title="Copy"
                                            data-testid="reset-pwd-copy"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-yellow-300/80 text-[11px] mt-1.5">
                                        ⚠️ Copy this password NOW — the user&apos;s old password will stop working immediately.
                                    </p>
                                </div>
                                <div className="flex justify-end gap-3 pt-2">
                                    <Button variant="ghost" onClick={() => setIsResetOpen(false)} className="text-white/60">
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleResetPassword}
                                        disabled={resetSaving}
                                        className="bg-yellow-500 hover:bg-yellow-500/80 text-black font-bold"
                                        data-testid="confirm-reset-btn"
                                    >
                                        {resetSaving ? "Resetting…" : <><KeyRound className="h-4 w-4 mr-2" /> Reset Password</>}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default UserManagement;
